import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import SceneFrame from '../three/SceneFrame';
import AnnouncementFeed from '../components/AnnouncementFeed';
import FlywheelSection from '../components/FlywheelSection';
import FishingIntelligenceSection from '../components/FishingIntelligenceSection';
import { Button, Eyebrow, SectionHeader, StatTile, Reveal, monoCaseStyle } from '../components/ui-kit';
import { useContent } from '../i18n/LocaleProvider';
import { getCached } from '../lib/api';
import { useApi } from '../lib/hooks';
import brand from '../brand/brand';

export default function Home() {
  const { HOME, UI } = useContent();
  const [hovered, setHovered] = useState(null);
  const navigate = useNavigate();
  const { data, loading } = useApi(() => getCached('/announcements', { limit: 3 }), []);
  const announcements = data?.items || [];

  return (
    <>
      <Seo
        title="Home"
        path="/"
        description={`${brand.legalName} — a multi-country group spanning marine, industrial, healthcare and scientific instrumentation, now building AI, automation and MarTech capabilities.`}
      />

      {/* A · Hero */}
      <SceneFrame
        load={() => import('../three/scenes/HeroStraitField')}
        poster="/posters/hero-strait-field.webp"
        ariaLabel="A slow-moving three-dimensional map of the Straits of Malacca with connecting lines between Singapore, Malaysia and Indonesia, representing the Group's regional footprint."
        rounded={false}
        className="hero"
        overlay={
          <>
            <div className="hero__scrim">
              <div className="u-container">
                <Eyebrow onNavy>{HOME.hero.eyebrow}</Eyebrow>
                <h1 className="t-display" style={{ color: '#fff', marginTop: 16, maxWidth: '20ch' }}>
                  {HOME.hero.h1}
                </h1>
                <p
                  className="t-body-lg t-on-navy-secondary"
                  style={{ marginTop: 24, maxWidth: '58ch' }}
                >
                  {HOME.hero.sub}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 32 }}>
                  <Button to={HOME.hero.primary.to} variant="primary" magnetic data-testid="hero-ir-cta">
                    {HOME.hero.primary.label}
                  </Button>
                  <Button to={HOME.hero.secondary.to} variant="secondary-on-navy">
                    {HOME.hero.secondary.label}
                  </Button>
                </div>
              </div>
            </div>
            <div className="hero__scroll-cue" aria-hidden="true">
              <span className="t-micro">{UI.home.scroll}</span>
              <span className="hero__scroll-line" />
            </div>
          </>
        }
      />

      {/* B · Latest from the Group — disclosure first, above all marketing content */}
      <section className="u-section-tight" aria-labelledby="latest-title" style={{ background: 'var(--white)' }}>
        <div className="u-container">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              alignItems: 'baseline',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <Eyebrow>{HOME.latest.eyebrow}</Eyebrow>
              <h2 id="latest-title" style={{ marginTop: 12, fontSize: 24 }}>
                {HOME.latest.heading}
              </h2>
            </div>
            <Link to={HOME.latest.viewAll.to} className="c-btn c-btn--tertiary">
              {HOME.latest.viewAll.label}
            </Link>
          </div>
          <div style={{ marginTop: 24 }}>
            <AnnouncementFeed
              items={announcements}
              loading={loading}
              emptyMessage={HOME.latest.empty}
            />
          </div>
        </div>
      </section>

      {/* C · Proof-point band */}
      <section className="u-section-tight" aria-label={UI.home.glanceAria}>
        <div className="u-container">
          <div className="stat-grid">
            {HOME.stats.map((stat) => (
              <StatTile
                key={stat.label}
                value={stat.value}
                suffix={stat.suffix}
                literal={stat.literal}
                label={stat.label}
              />
            ))}
          </div>
        </div>
      </section>

      {/* D · The Ecosystem */}
      <section className="u-navy u-section" aria-labelledby="ecosystem-title">
        <div className="u-container">
          <SectionHeader
            eyebrow={HOME.ecosystem.eyebrow}
            title={HOME.ecosystem.h2}
            intro={HOME.ecosystem.intro}
            id="ecosystem-title"
            onNavy
          />

          <div style={{ marginTop: 48 }}>
            <SceneFrame
              load={() => import('../three/scenes/PillarMonoliths')}
              poster="/posters/pillar-monoliths.webp"
              ariaLabel="Three tall monoliths of navy glass on a dark reflective plane: one filled with dense horizontal strata, one with a lattice of connected glowing nodes, and one with a vertical chain of linked cubes."
              style={{ aspectRatio: '21 / 9', width: '100%' }}
              sceneProps={{
                hoveredKey: hovered,
                onHover: setHovered,
                onSelect: (key) => navigate(`/our-businesses#${key}`),
              }}
            />
          </div>

          <div className="pillar-grid" style={{ marginTop: 24 }}>
            {HOME.ecosystem.cards.map((card, index) => (
              <Reveal key={card.key} delay={index * 60}>
                <article
                  className="c-card-navy c-card-navy--hover"
                  onMouseEnter={() => setHovered(card.key)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(card.key)}
                  onBlur={() => setHovered(null)}
                  style={{ height: '100%' }}
                  data-testid={`pillar-card-${card.key}`}
                >
                  <p className="t-micro" style={{ color: 'var(--text-link-on-navy)' }}>
                    {card.label}
                  </p>
                  <h3 style={{ marginTop: 12 }}>{card.title}</h3>
                  <p className="t-small t-on-navy-secondary" style={{ marginTop: 12 }}>
                    {card.copy}
                  </p>
                  <Link
                    to={card.link.to}
                    className="c-btn c-btn--tertiary-on-navy"
                    style={{ marginTop: 16 }}
                  >
                    {card.link.label}
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* E · The Flywheel */}
      <FlywheelSection />

      {/* F · Technology in the water — the fishing application */}
      <FishingIntelligenceSection />

      {/* G · Where technology meets the Group */}
      <section className="u-navy-depth u-section" aria-labelledby="tech-band-title">
        <div className="u-container">
          <SectionHeader
            eyebrow={HOME.techBand.eyebrow}
            title={HOME.techBand.h2}
            id="tech-band-title"
            onNavy
          />
          <div className="tile-grid tile-grid--3" style={{ marginTop: 40 }}>
            {HOME.techBand.tiles.map((tile, index) => (
              <Reveal key={tile.key} delay={index * 60}>
                <article className="c-card-navy" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>
                  <img
                    src={tile.poster}
                    alt={tile.posterAlt}
                    width={1600}
                    height={900}
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', aspectRatio: '3 / 2', objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{ padding: 'var(--space-lg)' }}>
                    <p className="t-micro" style={{ color: 'var(--text-link-on-navy)', ...monoCaseStyle(tile.eyebrow) }}>
                      {tile.eyebrow}
                    </p>
                    <h3 style={{ marginTop: 10, fontSize: 19 }}>{tile.title}</h3>
                    <p className="t-small t-on-navy-secondary" style={{ marginTop: 10 }}>
                      {tile.copy}
                    </p>
                    {tile.link.to.startsWith('/#') ? (
                      <a href={tile.link.to.slice(1)} className="c-btn c-btn--tertiary-on-navy" style={{ marginTop: 14 }}>
                        {tile.link.label}
                      </a>
                    ) : (
                      <Link to={tile.link.to} className="c-btn c-btn--tertiary-on-navy" style={{ marginTop: 14 }}>
                        {tile.link.label}
                      </Link>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* H · Investor CTA band */}
      <section className="u-navy u-section" aria-labelledby="investor-cta-title">
        <div className="u-container" style={{ maxWidth: 880, textAlign: 'center' }}>
          <h2 id="investor-cta-title">{HOME.cta.h2}</h2>
          <p className="t-body-lg t-on-navy-secondary" style={{ marginTop: 16 }}>
            {HOME.cta.copy}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
            <Button to={HOME.cta.button.to} variant="primary" magnetic>
              {HOME.cta.button.label}
            </Button>
          </div>
          <p className="t-mono t-on-navy-secondary" style={{ marginTop: 20, fontSize: 12 }}>
            {HOME.cta.micro}
          </p>
        </div>
      </section>
    </>
  );
}
